import { useEffect, useState } from 'preact/hooks';
import { get, post, put } from '../api.js';
import { Link } from '../router.js';
import { Field, useForm } from '../ui/Field.jsx';
import { addDays, fmtRange, isDate } from '../../lib/dates.js';

const KIND = { tura: 'Tura', projekat: 'Projekat' };
const NEW_TOUR = { name: '', kind: 'tura', days: '1' };

function TourFields({ bind }) {
  return (
    <>
      <Field label="Naziv"><input {...bind('name')} autocomplete="off" required /></Field>
      <Field label="Vrsta">
        <select {...bind('kind')}>
          <option value="tura">Tura</option>
          <option value="projekat">Projekat</option>
        </select>
      </Field>
      <Field label="Broj dana"><input {...bind('days')} type="number" min="1" max="60" inputmode="numeric" required /></Field>
    </>
  );
}

// "Nova tura": a catalogue tour on dates; Kraj follows Početak and the tour's length until changed by hand.
function NewDeparture({ tours, onSaved, onCancel }) {
  const [f, bind, setF] = useForm({ tourId: '', start: '', end: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const follow = (k) => (e) => {
    const next = { ...f, [k]: e.currentTarget.value };
    const tour = tours.find((t) => String(t.id) === next.tourId);
    if (tour && isDate(next.start)) next.end = addDays(next.start, tour.days - 1);
    setF(next);
  };

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post('/departures', f);
      await onSaved();
      return;
    } catch (err) {
      setError(err);
    }
    setBusy(false);
  };

  if (tours.length === 0) return <p class="empty">Prvo dodaj turu u ponudu, ispod.</p>;
  return (
    <form class="panel inline-form" aria-label="Nova tura" onSubmit={save}>
      <Field label="Tura">
        <select {...bind('tourId')} onInput={follow('tourId')} required>
          <option value="" disabled>Izaberi turu</option>
          {tours.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
        </select>
      </Field>
      <Field label="Početak"><input {...bind('start')} onInput={follow('start')} type="date" required /></Field>
      <Field label="Kraj"><input {...bind('end')} type="date" min={f.start || undefined} required /></Field>
      {error && (
        <p class="alert" role="alert">
          {error.message}{' '}
          {error.details?.departureId && <Link href={`/ture/${error.details.departureId}`}>Otvori je</Link>}
        </p>
      )}
      <div class="actions">
        <button class="btn" disabled={busy}>Sačuvaj turu</button>
        <button type="button" class="btn-quiet" onClick={onCancel}>Odustani</button>
      </div>
    </form>
  );
}

function EditTour({ tour, onSaved, onCancel }) {
  const [f, bind] = useForm({ name: tour.name, kind: tour.kind, days: String(tour.days) });
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await put(`/tours/${tour.id}`, f);
      await onSaved();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <tr>
      <td colSpan={4} class="cell-form">
        <form class="inline-form" aria-label="Izmeni turu" onSubmit={save}>
          <TourFields bind={bind} />
          {error && <p class="alert" role="alert">{error}</p>}
          <div class="actions">
            <button class="btn">Sačuvaj izmene</button>
            <button type="button" class="btn-quiet" onClick={onCancel}>Odustani</button>
          </div>
        </form>
      </td>
    </tr>
  );
}

export function ToursOffice() {
  const [departures, setDepartures] = useState(null);
  const [tours, setTours] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [editing, setEditing] = useState(null);
  const [add, bindAdd, setAdd] = useForm(NEW_TOUR);
  const [addError, setAddError] = useState(null);

  const load = () => Promise.all([get('/departures'), get('/tours')]).then(
    ([d, t]) => { setDepartures(d.departures); setTours(t.tours); setLoadError(null); },
    (e) => setLoadError(e.message),
  );
  useEffect(() => { load(); }, []);

  const addTour = async (e) => {
    e.preventDefault();
    setAddError(null);
    try {
      await post('/tours', add);
      setAdd(NEW_TOUR);
      await load();
    } catch (err) {
      setAddError(err.message);
    }
  };

  return (
    <>
      <div class="page-head"><h1>Ture</h1></div>
      {loadError && <p class="alert" role="alert">{loadError}</p>}

      <section class="section" aria-labelledby="po-datumima">
        <div class="section-head">
          <h2 id="po-datumima">Ture po datumima</h2>
          {tours && !planning && <button class="btn" onClick={() => setPlanning(true)}>Nova tura</button>}
        </div>
        {planning && tours && (
          <NewDeparture tours={tours} onCancel={() => setPlanning(false)} onSaved={async () => { setPlanning(false); await load(); }} />
        )}
        {departures && departures.length === 0 && <p class="empty">Nema zakazanih tura. Dodaj prvu: Nova tura.</p>}
        {departures && departures.length > 0 && (
          <table class="stack">
            <thead>
              <tr><th>Datum</th><th>Tura ili projekat</th><th class="num">Ljudi</th><th>Vodiči</th></tr>
            </thead>
            <tbody>
              {departures.map((d) => (
                <tr key={d.id}>
                  <td data-label="Datum">{fmtRange(d.start_date, d.end_date)}</td>
                  <td data-label="Tura ili projekat"><Link href={`/ture/${d.id}`}>{d.tour.name}</Link></td>
                  <td data-label="Ljudi" class="num">{d.people}</td>
                  <td data-label="Vodiči">
                    {d.guides.length ? <span>{d.guides.map((g) => g.name).join(', ')}</span> : <span class="no-guide">bez vodiča</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section class="section" aria-labelledby="ponuda">
        <h2 id="ponuda">Ponuda</h2>
        {tours && tours.length === 0 && <p class="empty">Ponuda je prazna. Dodaj prvu turu ili projekat.</p>}
        {tours && tours.length > 0 && (
          <table class="stack">
            <thead>
              <tr><th>Naziv</th><th>Vrsta</th><th class="num">Dana</th><th><span class="sr-only">Izmena</span></th></tr>
            </thead>
            <tbody>
              {tours.map((t) => (editing === t.id
                ? <EditTour key={t.id} tour={t} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
                : (
                  <tr key={t.id}>
                    <td data-label="Naziv">{t.name}</td>
                    <td data-label="Vrsta">{KIND[t.kind]}</td>
                    <td data-label="Dana" class="num">{t.days}</td>
                    <td class="cell-action"><button class="btn-quiet" onClick={() => setEditing(t.id)}>Izmeni</button></td>
                  </tr>
                )))}
            </tbody>
          </table>
        )}
        <form class="panel inline-form add-tour" aria-label="Dodaj u ponudu" onSubmit={addTour}>
          <TourFields bind={bindAdd} />
          {addError && <p class="alert" role="alert">{addError}</p>}
          <div class="actions"><button class="btn">Dodaj u ponudu</button></div>
        </form>
      </section>
    </>
  );
}
